<script>
  import { format } from 'svelte-i18n';
  import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';
  import { Spinner } from '@client/components/spinner';

  import {
    allDomains,
    billingDomains,
    DOMAIN_SECTIONS,
    hasBillingDomains,
    loadMoreDomains,
    mdmDomains,
    retryDomains,
    searchDomains
  } from '@client/stores/domain-list';
  import { billingId } from '@client/stores/domain-settings';
  import { switchDomain } from '@client/stores/domain-switch';
  import { userStore } from '@client/stores/user';

  import { debounce } from '@common/utils/debounce';

  export let variant = 'sidebar'; // 'sidebar' | 'managing' -- class hook only
  export let caret = false;
  export let nav = true;
  export let onSwitched = () => {};

  // getContext is initialization-only, so this cannot move inside an async handler.
  const { addNotification } = getNotificationsContext();

  let open = false;
  let query = '';

  // debounce() returns a bare function with no cancel, so guard the callback rather than the timer.
  const runSearch = debounce((value) => searchDomains(value), 300);

  const onSearchInput = (event) => {
    query = event.target.value;
    runSearch(query);
  };

  const onClearSearch = () => {
    query = '';
    searchDomains('');
  };

  const onSelect = async (domainname) => {
    const result = await switchDomain(domainname);

    if (result.ok) {
      open = false;
      query = '';
      onSwitched(result.domain);
      return;
    }

    if (result.reason === 'superseded') {
      return;
    }

    const label = result.reason === 'not-eligible' ? 'label.SWITCH_DOMAIN_NOT_ALLOWED' : 'label.SWITCH_DOMAIN_FAILED';

    addNotification({
      text: $format(label, { values: { domain: domainname } }),
      type: 'error',
      autohide: 2500,
      position: 'top-right'
    });
  };

  // The scopes overlap: list a domain once, under the first section it qualifies for.
  $: mdmNames = new Set($mdmDomains.items.map(({ domainname }) => domainname));
  $: billingVisible = $billingDomains.items.filter(({ domainname }) => !mdmNames.has(domainname));
  $: listedAbove = new Set([...mdmNames, ...$billingDomains.items.map(({ domainname }) => domainname)]);
  $: allVisible = $allDomains.items.filter(({ domainname }) => !listedAbove.has(domainname));

  // Hidden rather than rendered empty. MDM always renders, so the empty/no-match message has a home.
  const hasSomethingToShow = (state, visible) => state.loading || state.error || visible.length > 0;

  $: sections = [
    {
      name: DOMAIN_SECTIONS.MDM,
      label: $format('label.MDM_ACCOUTNS'),
      state: $mdmDomains,
      items: $mdmDomains.items,
      show: true
    },
    {
      name: DOMAIN_SECTIONS.BILLING,
      // Without a staff record this repeats the MDM scope, which the dedupe then hides -- so a
      // rendered section means the billing constraint applied.
      label: $format('label.BILLING_ID_NUMBER', { values: { id: $billingId } }),
      state: $billingDomains,
      items: billingVisible,
      show: $hasBillingDomains && hasSomethingToShow($billingDomains, billingVisible)
    },
    {
      name: DOMAIN_SECTIONS.ALL,
      label: $format('label.ALL_DOMAINS'),
      state: $allDomains,
      items: allVisible,
      show: hasSomethingToShow($allDomains, allVisible)
    }
  ];
</script>

<!-- `nav` on <Dropdown> swaps Popper for static positioning; it belongs on the toggle only. -->
<Dropdown class="domain-switcher domain-switcher--{variant}" isOpen={open} toggle={() => (open = !open)}>
  <DropdownToggle {nav} {caret}>
    <slot name="toggle">{$userStore.domain}</slot>
  </DropdownToggle>
  <DropdownMenu end class="domain-switcher-menu">
    <div class="domain-switcher-search">
      <Icon name="search" />
      <input
        type="search"
        autocomplete="off"
        value={query}
        on:input={onSearchInput}
        placeholder={$format('label.SEARCH_DOMAINS')}
      />
      {#if query.length}
        <Icon name="x" role="button" tabindex="0" on:click={onClearSearch} />
      {/if}
    </div>

    {#each sections as section}
      {#if section.show}
        {#if section.name !== DOMAIN_SECTIONS.MDM}
          <DropdownItem divider />
        {/if}

        <DropdownItem header>
          {section.label}
          {#if section.items.length}
            ({section.items.length})
          {/if}
        </DropdownItem>

        {#each section.items as domain}
          <DropdownItem
            class={domain.domainname === $userStore.domain ? 'active' : ''}
            on:click={() => onSelect(domain.domainname)}
          >
            {domain.domainname}
          </DropdownItem>
        {/each}

        {#if section.state.loading}
          <div class="empty">
            <Spinner show inline styles="border-width: 2px; color: #0087ff; height: 14px; width: 14px;" />
          </div>
        {:else if section.state.error}
          <div class="domain-switcher-status domain-switcher-error">
            <Icon name="alert-triangle" />
            <span>{$format('label.DOMAIN_LIST_FAILED')}</span>
            <Button color="link" size="sm" on:click={() => retryDomains(section.name)}>
              {$format('label.RETRY')}
            </Button>
          </div>
        {:else if section.state.loaded && !section.items.length}
          <div class="empty">
            {#if query.trim()}
              {$format('label.NO_DOMAINS_MATCH')}
            {:else}
              {$format('label.NO_DOMAINS_AVAILABLE')}
            {/if}
          </div>
        {:else if section.state.items.length < section.state.total}
          <div class="domain-switcher-status">
            <Button color="link" size="sm" on:click={() => loadMoreDomains(section.name)}>
              {$format('label.LOAD_MORE_DOMAINS', {
                values: { shown: section.state.items.length, total: section.state.total }
              })}
            </Button>
          </div>
        {/if}
      {/if}
    {/each}
  </DropdownMenu>
</Dropdown>

<style lang="scss" src="./domain-switcher.scss"></style>
